<template>
  <div class="vacations-chart" :data-frame="frame">
    <c-tabs
      class="vacations-chart__frames"
      :active="frame"
      :items="[
        {
          label: 'Неделя',
          value: 'week',
        },
        {
          label: 'Месяц',
          value: 'month',
        },
        {
          label: 'Таймлайн',
          value: 'timeline',
        },
      ]"
      @change="setFrame"
    />

    <div class="vacations-chart__group-by">
      <c-input
        v-model="groupBy"
        type="select"
        :items="[
          { value: 'filials', label: 'По филиалам' },
          { value: 'posts', label: 'По должностям' },
        ]"
        :clearable="true"
        placeholder="Без группировки"
      ></c-input>
    </div>

    <position-watcher v-model="position" :offset="true">
      <div class="vacations-chart__border">
        <div class="vacations-chart__container">
          <div v-if="frame !== 'timeline'" class="vacations-chart__frame frame">
            <span class="frame-text" v-html="frameText"></span>

            <div v-if="frame !== 'timeline'" class="frame-controls">
              <button class="frame-control" :disabled="!hasPrevFrame" @click="prevFrame">
                <svg-icon name="chevron-left" :size="14"></svg-icon>
              </button>
              <button class="frame-control" :disabled="!hasNextFrame" @click="nextFrame">
                <svg-icon name="chevron-right" :size="14"></svg-icon>
              </button>
            </div>
          </div>

          <perfect-scrollbar
            ref="scroll"
            class="vacations-chart__scroll"
            @ps-scroll-left="onScrollLeft"
            @ps-scroll-right="onScrollRight"
          >
            <div ref="wrapper" class="vacations-chart__wrapper">
              <div
                ref="header"
                class="vacations-chart-header"
                :class="{
                  fixed: position.top === -1,
                }"
                :style="{
                  transform: `translateY(${position.topOffset > 0 ? position.topOffset - 2 : 0}px)`,
                }"
              >
                <div ref="offset" class="vacations-chart-header__cell"></div>
                <div class="vacations-chart-header__days calendar">
                  <div
                    v-for="(d, i) in days"
                    :key="i"
                    class="vacations-chart-header__day calendar-day"
                    :data-day="d.date.format('DD.MM.YYYY')"
                    :data-month="d.date.format('M')"
                    :data-date="d.date.format('D')"
                    :class="{
                      'calendar-day--off': d.dayOff,
                    }"
                  >
                    <span>
                      {{ d.date.format("D") }}
                      <span v-if="frame === 'week'" class="short-month" :data-month="d.date.format('M')"></span>
                    </span>
                    <span v-if="frame === 'week'" class="week-day" :data-day="i + 1"></span>
                    <div
                      v-if="frame === 'timeline'"
                      class="calendar-month"
                      :style="{
                        width: `${d.tillMonthEnd * 100}%`,
                      }"
                    >
                      <div
                        class="month"
                        :data-date="d.date.format('D')"
                        :data-month="d.date.format('M')"
                        :data-year="d.date.format('YYYY')"
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
              <template v-if="groupBy">
                <div v-if="!groupsLoading && !groups.length" class="workers-list-message">
                  <div class="workers-list-message__panel workers-list__empty">
                    За текущий период отпуска и больничные не найдены
                  </div>
                </div>
                <div v-if="!groupsLoading && !groups.length" class="workers-list-message">
                  <div class="workers-list-message__panel workers-list__loading">
                    <Loader v-if="groupsLoading"></Loader>
                  </div>
                </div>

                <workers-group
                  v-for="group in groups"
                  :key="group.id"
                  ref="groups"
                  :group="group"
                  :days="days"
                  :frame="frame !== 'timeline'"
                  :group-by="groupBy.value === 'filials' ? 'filial_id' : 'post_code'"
                  :search-params="searchParams"
                  :can-edit="canEdit"
                  :show-all="!searchParams.periodStart"
                ></workers-group>
              </template>
              <template v-else>
                <workers-list
                  ref="list"
                  :days="days"
                  :frame="frame !== 'timeline'"
                  :can-edit="canEdit"
                  :show-all="!searchParams.periodStart"
                ></workers-list>
              </template>
            </div>
          </perfect-scrollbar>
        </div>
      </div>
    </position-watcher>
  </div>
</template>

<script>
import moment from "moment";
import PositionWatcher from "@/components/position-watcher";
import SvgIcon from "@/components/svg-icon";
import CInput from "@/components/controls2/Input";
import CTabs from "@/components/tabs";
import WorkersList from "./workers-list";
import WorkersGroup from "./workers-group";

const MONTHS_IN_PERIOD = 2;
const MAX_PERIOD_COUNT = 2;

export default {
  components: {
    WorkersList,
    WorkersGroup,
    CInput,
    PositionWatcher,
    SvgIcon,
    CTabs,
  },

  props: ["canEdit"],

  data() {
    return {
      inited: false,
      searchParams: {},

      monthNames: [
        ["Январь"],
        ["Февраль"],
        ["Март"],
        ["Апрель"],
        ["Май"],
        ["Июнь"],
        ["Июль"],
        ["Август"],
        ["Сентябрь"],
        ["Октябрь"],
        ["Ноябрь"],
        ["Декабрь"],
      ],

      // ограничение фильтра
      periodLimitFrom: null,
      periodLimitTo: null,

      // отображаемый период
      visiblePeriod: {
        from: null,
        to: null,
      },

      days: [],
      months: [],

      // группировка по неделям или месяцам
      frame: "timeline",

      // группировка по филиалам или должностям
      groupBy: null,
      groupsLoading: false,

      collections: {
        filials: [],
        posts: [],
      },

      topPosition: 1,
      bottomPosition: 1,

      position: { top: 1, bottom: 1 },
    };
  },

  computed: {
    frameText() {
      if (this.frame === "timeline") return "";
      if (!this.visiblePeriod.from) return "";
      if (this.frame === "month") {
        const month = this.visiblePeriod.from.get("month");

        const year = this.visiblePeriod.from.get("year");
        return `${this.monthNames[month][0]} ${year}`;
      }
      return `${this.visiblePeriod.from.format("DD.MM.YYYY")}&nbsp;– ${this.visiblePeriod.to.format("DD.MM.YYYY")}`;
    },
    fixScroll() {
      return this.position.top > 1 && this.position.bottom === 1;
    },
    groups() {
      let groups = [];
      if (!this.groupBy) return groups;

      if (this.groupBy.value === "filials") groups = this.collections.filials;
      if (this.groupBy.value === "posts") groups = this.collections.posts;

      if (this.searchParams.periodStart) {
        groups = groups.filter((g) => g.number_with_vacation > 0);
      }

      return groups;
    },
    daysCount() {
      return this.days.length;
    },
    hasPrevFrame() {
      if (!this.searchParams.periodStart) return true;
      return moment(this.searchParams.periodStart, "DD.MM.YYYY").isBefore(this.visiblePeriod.from);
    },
    hasNextFrame() {
      if (!this.searchParams.periodEnd) return true;
      return moment(this.searchParams.periodEnd, "DD.MM.YYYY").isAfter(this.visiblePeriod.to);
    },
  },

  watch: {
    searchParams(v) {
      if (!this.groupBy) {
        this.$refs.list.update(v);
      }
    },
    groupBy(v) {
      if (!this.inited) return;

      const query = {
        ...this.$route.query,
        groupBy: v && v.value,
      };
      this.$router
        .replace({
          name: "vacations-chart",
          query,
        })
        .catch(() => {});
      if (!v) {
        this.$nextTick(() => this.$refs.list.update(this.searchParams));
      }
    },
    frame(v) {
      if (!this.inited) return;

      const query = {
        ...this.$route.query,
        frame: v,
      };
      this.$router
        .replace({
          name: "vacations-chart",
          query,
        })
        .catch(() => {});
    },
  },

  created() {
    const { query } = this.$route;
    if (query.groupBy === "filials") {
      this.groupBy = {
        value: "filials",
        label: "По филиалам",
      };
    } else if (query.groupBy === "posts") {
      this.groupBy = {
        value: "posts",
        label: "По должностям",
      };
    }

    if (query.frame === "week" || query.frame === "month") {
      this.frame = query.frame;
    }

    this.inited = true;
    this.$emit("inited");
  },

  updated() {
    if (this._lastDayToScroll) {
      const $scroll = this.$refs.scroll.$el;
      const $wrapper = this.$refs.wrapper;
      const $day = document.querySelector(`[data-day='${this._lastDayToScroll}']`);

      if ($day) {
        const scrollRect = $scroll.getBoundingClientRect();
        const wrapperRect = $wrapper.getBoundingClientRect();
        const dayRect = $day.getBoundingClientRect();

        const scroll = wrapperRect.left - dayRect.left + scrollRect.width - dayRect.width;
        this.updateScroll(-1 * scroll);
      }

      this.updating = false;
      this._lastDayToScroll = undefined;

      return;
    }

    if (this._firstDayToScroll) {
      const $wrapper = this.$refs.wrapper;
      const $day = document.querySelector(`[data-day='${this._firstDayToScroll}']`);
      const $offset = this.$refs.offset;

      if ($day) {
        const offsetRect = $offset.getBoundingClientRect();
        const wrapperRect = $wrapper.getBoundingClientRect();
        const dayRect = $day.getBoundingClientRect();

        const scroll = wrapperRect.left - dayRect.left + offsetRect.width;
        this.updateScroll(-1 * scroll);
      } else {
        this.updateScroll(0);
      }

      this.updating = false;
      this._firstDayToScroll = undefined;
    }
  },

  methods: {
    // расчет начального видимого отрезка на основе фильтров
    calculateStartVisiblePeriod() {
      const now = moment();
      let from;
      let to;

      // неделя
      if (this.frame === "week") {
        if (this.periodLimitFrom) {
          from = moment(this.periodLimitFrom, "DD.MM.YYYY").startOf("week");
          to = from.clone().endOf("week");
        } else {
          from = now.clone().startOf("week");
          to = now.clone().endOf("week");
        }
      }
      // месяц
      else if (this.frame === "month") {
        if (this.periodLimitFrom) {
          from = moment(this.periodLimitFrom, "DD.MM.YYYY").startOf("month");
          to = from.clone().endOf("month");
        } else {
          from = now.clone().startOf("month");
          to = now.clone().endOf("month");
        }
      }
      // таймлайн
      else if (this.periodLimitFrom) {
        from = moment(this.periodLimitFrom, "DD.MM.YYYY");
        to = moment(this.periodLimitTo, "DD.MM.YYYY");
      } else {
        from = now.clone().subtract(MONTHS_IN_PERIOD, "months").startOf("month");
        to = from
          .clone()
          .add(MONTHS_IN_PERIOD * 2, "months")
          .set("date", 0);

        this._firstDayToScroll = now.clone().startOf("month").format("DD.MM.YYYY");
      }

      this.visiblePeriod = {
        from,
        to,
      };
    },

    // передвинуть видимый период назад
    shiftVisiblePeriodBack() {
      if (this.frame !== "timeline") return;
      if (this.periodLimitFrom) return;

      this.updating = true;
      this._firstDayToScroll = this.days[0].date.format("DD.MM.YYYY");

      const currentVisiblePeriodFrom = this.visiblePeriod.from;
      let currentVisiblePeriodTo = this.visiblePeriod.to;

      const newVisiblePeriodFrom = currentVisiblePeriodFrom
        .clone()
        .startOf("month")
        .subtract("months", MONTHS_IN_PERIOD);

      const limitTo = newVisiblePeriodFrom
        .clone()
        .add(MAX_PERIOD_COUNT * MONTHS_IN_PERIOD - 1, "months")
        .endOf("month");

      if (currentVisiblePeriodTo.isAfter(limitTo)) currentVisiblePeriodTo = limitTo;

      this.visiblePeriod = {
        from: newVisiblePeriodFrom,
        to: currentVisiblePeriodTo,
      };

      this.calculateDays();

      if (!this.groupBy) {
        this.$refs.list.shiftBack({
          periodStart: newVisiblePeriodFrom.format("YYYY-MM-DD"),
          periodEnd: currentVisiblePeriodFrom.clone().subtract("day", 1).format("YYYY-MM-DD"),
        });
      } else {
        this.$refs.groups.forEach((g) => {
          g.$refs.list.shiftBack({
            periodStart: newVisiblePeriodFrom.format("YYYY-MM-DD"),
            periodEnd: currentVisiblePeriodFrom.clone().subtract("day", 1).format("YYYY-MM-DD"),
          });
        });
      }
    },

    // передвинуть видимый период вперед
    shiftVisiblePeriodForward() {
      if (this.frame !== "timeline") return;
      if (this.periodLimitTo) return;

      this.updating = true;
      this._lastDayToScroll = this.days[this.days.length - 1].date.format("DD.MM.YYYY");

      let currentVisiblePeriodFrom = this.visiblePeriod.from;
      const currentVisiblePeriodTo = this.visiblePeriod.to;

      const newVisiblePeriodTo = currentVisiblePeriodTo
        .clone()
        .startOf("month")
        .add("months", MONTHS_IN_PERIOD)
        .endOf("month");

      const limitFrom = newVisiblePeriodTo
        .clone()
        .startOf("month")
        .subtract(MAX_PERIOD_COUNT * MONTHS_IN_PERIOD - 1, "months")
        .startOf("month");

      if (currentVisiblePeriodFrom.isBefore(limitFrom)) currentVisiblePeriodFrom = limitFrom;

      this.visiblePeriod = {
        from: currentVisiblePeriodFrom,
        to: newVisiblePeriodTo,
      };
      this.calculateDays();

      if (!this.groupBy) {
        this.$refs.list.shiftForward({
          periodStart: currentVisiblePeriodTo.clone().add("day", 1).format("YYYY-MM-DD"),
          periodEnd: newVisiblePeriodTo.format("YYYY-MM-DD"),
        });
      } else {
        this.$refs.groups.forEach((g) => {
          g.$refs.list.shiftForward({
            periodStart: currentVisiblePeriodTo.clone().add("day", 1).format("YYYY-MM-DD"),
            periodEnd: newVisiblePeriodTo.format("YYYY-MM-DD"),
          });
        });
      }
    },

    // разбить видимый период на дни
    calculateDays() {
      if (!this.visiblePeriod.from && !this.visiblePeriod.to) return [];

      const days = [];

      let currentDate = this.visiblePeriod.from.clone().startOf("day");

      while (currentDate.isSameOrBefore(this.visiblePeriod.to)) {
        const weekday = currentDate.get("weekday");

        let monthEnd = currentDate.clone().endOf("month");

        if (this.visiblePeriod.to.isBefore(monthEnd)) {
          monthEnd = this.visiblePeriod.to;
        }

        const day = {
          tillMonthEnd: monthEnd.get("date") - currentDate.get("date") + 1,
          date: currentDate,
          dayOff: weekday === 5 || weekday === 6,
        };
        days.push(day);
        currentDate = currentDate.clone().add(1, "d");
      }

      this.days = days;

      const months = [];
      let currentMonth;

      this.days.forEach((day) => {
        const currentDate = day.date;

        const month = currentDate.get("month") + 1;
        const year = currentDate.get("year");

        if (currentMonth && currentMonth.month === month) {
          currentMonth.days.push(day);
        } else {
          if (currentMonth) months.push(currentMonth);
          currentMonth = {
            month,
            year,
            days: [day],
          };
        }
      });

      if (currentMonth) months.push(currentMonth);
      this.months = months;

      return [];
    },

    // обновление фильтров
    update(filters) {
      this.periodLimitFrom = filters.periodStart;
      this.periodLimitTo = filters.periodEnd;

      this.calculateStartVisiblePeriod();
      this.calculateDays();

      const params = {
        ...filters,
        from_dt: this.visiblePeriod.from.format("YYYY-MM-DD"),
        to_dt: this.visiblePeriod.to.format("YYYY-MM-DD"),
      };

      this.getGroups(params).then(() => {
        this.$nextTick(() => {
          this.searchParams = params;
        });
      });
    },

    regroup() {
      this.calculateStartVisiblePeriod();
      this.calculateDays();
    },

    // горизонтальный скролл
    onScrollLeft() {
      if (this.updating) return;

      const { x } = this.$refs.scroll.ps.reach;
      if (x === "start") this.shiftVisiblePeriodBack();
    },

    onScrollRight() {
      if (this.updating) return;

      const { x } = this.$refs.scroll.ps.reach;
      if (x === "end") this.shiftVisiblePeriodForward();
    },

    updateScroll(scrollLeft) { 
      const { scroll } = this.$refs;
      scroll.destroy();
      scroll.create();
      scroll.$el.scrollLeft = scrollLeft;
    },

    // группы
    getGroups(filters) {
      this.groupsLoading = true;
      return this.$store.dispatch("vacationsChart/getGroups", filters).then((groups) => {
        this.collections = groups;
        this.groupsLoading = false;
      });
    },

    // Фреймы (неделя/месяц/таймлайн)
    setFrame(frame) {
      this.frame = frame;

      this.calculateStartVisiblePeriod();
      this.calculateDays();

      const params = {
        ...this.searchParams,
        from_dt: this.visiblePeriod.from.format("YYYY-MM-DD"),
        to_dt: this.visiblePeriod.to.format("YYYY-MM-DD"),
      };

      this.getGroups(params).then(() => {
        this.$nextTick(() => {
          this.searchParams = params;
        });
      });
    },

    prevFrame() {
      if (this.frame === "timeline") return;

      const to = this.visiblePeriod.from.clone().subtract(1, "d");
      const from = to.clone().startOf(this.frame);

      this.visiblePeriod = {
        from,
        to,
      };

      this.calculateDays();

      const params = {
        ...this.searchParams,
        from_dt: from.format("YYYY-MM-DD"),
        to_dt: to.format("YYYY-MM-DD"),
      };

      this.getGroups(params).then(() => {
        this.$nextTick(() => {
          this.searchParams = params;
        });
      });
    },

    nextFrame() {
      if (this.frame === "timeline") return;

      const from = this.visiblePeriod.to.clone().add(1, "d");
      const to = from.clone().endOf(this.frame);

      this.visiblePeriod = {
        from,
        to,
      };

      this.calculateDays();

      const params = {
        ...this.searchParams,
        from_dt: from.format("YYYY-MM-DD"),
        to_dt: to.format("YYYY-MM-DD"),
      };

      this.getGroups(params).then(() => {
        this.$nextTick(() => {
          this.searchParams = params;
        });
      });
    },
  },
};
</script>

<style scoped lang="scss" src="./style.scss"></style>
